import { FC, Fragment, useMemo } from 'react'
import { bigFromFee, formatPrice, valueAfterDiscount } from 'utils/price'

import { FeeDTO } from 'models/fee'
import styles from './InstructionPrice.module.sass'
import { usePurchaseFlowConfig } from 'components/pages/PurchaseFlow/_main/contexts'

interface Props {
  feePrice: FeeDTO
}

/**
 * Displays formatted price of instruction including organization discount and currency.
 * 
 * DEPENDENCIES:
 * - InstructionStepStoreProvider
 * 
 * @example
 * <InstructionPrice feePrice={instructionOption.feePrice} />
 */
export const InstructionPrice: FC<Props> = ({ feePrice }) => {

  const { catalogueCurrency, catalogueDiscount } = usePurchaseFlowConfig()
  const priceAfterDiscount = useMemo(() => valueAfterDiscount(feePrice, catalogueDiscount), [catalogueDiscount, feePrice])

  // Fall back to the fee's own currency when the legacy `catalogueCurrency`
  // (sourced from `getProductCatalogue`) hasn't been populated. This happens
  // in the PF revamp where the legacy product-catalogue mutation never fires
  // — without this fallback, `formatPrice(<Big>, undefined)` drops the
  // currency symbol and renders bare `10.00` instead of `€10.00`.
  // Phase E-B.4 — Instructions step audit (2026-05-04 / qa-audit #7,#12).
  const displayCurrency = catalogueCurrency ?? feePrice.currency

  if (bigFromFee(feePrice).eq(0)) return <hr />

  return (
    <Fragment>
      {catalogueDiscount.gt(0) &&
        <span className={`${styles.lineThrough} ${styles.grayText} fs-mask`}>{formatPrice(feePrice, displayCurrency)}</span>
      }
      <strong className="fs-mask">{formatPrice(priceAfterDiscount, displayCurrency)}</strong>
    </Fragment>
  )

}
